import { useState, useEffect, useCallback } from "react";
import { fetchJson } from "@/utils/api";
import type { TemplateView, TemplateListResponse } from "@/types/api";
import Spinner from "@/components/ui/Spinner";
import { Search, PenLine, Sparkles, ListChecks, Users } from "lucide-react";

type SortMode = "newest" | "popular";

interface CategoryBrowserProps {
  onSelectTemplate: (template: TemplateView) => void;
  onCreateCustom: () => void;
  onGenerateWithAi?: () => void;
}

export default function CategoryBrowser({
  onSelectTemplate,
  onCreateCustom,
  onGenerateWithAi,
}: CategoryBrowserProps) {
  const [sort, setSort] = useState<SortMode>("newest");
  const [templates, setTemplates] = useState<TemplateView[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | undefined>();
  const [search, setSearch] = useState("");

  const fetchTemplates = useCallback(
    async (cursor?: string) => {
      const params = new URLSearchParams({ sort, limit: "12" });
      if (cursor) params.set("cursor", cursor);

      const data = await fetchJson<TemplateListResponse>(
        `/item-templates?${params.toString()}`,
      );
      return data;
    },
    [sort],
  );

  useEffect(() => {
    setLoading(true);
    fetchTemplates()
      .then((data) => {
        setTemplates(data.templates);
        setNextCursor(data.nextCursor);
      })
      .catch(() => {
        setTemplates([]);
        setNextCursor(undefined);
      })
      .finally(() => setLoading(false));
  }, [fetchTemplates]);

  const handleLoadMore = async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const data = await fetchTemplates(nextCursor);
      setTemplates((prev) => [...prev, ...data.templates]);
      setNextCursor(data.nextCursor);
    } catch {
      // Ignore load-more errors
    } finally {
      setLoadingMore(false);
    }
  };

  const filtered = search.trim()
    ? templates.filter((t) => {
        const q = search.toLowerCase();
        return (
          t.name.toLowerCase().includes(q) ||
          t.description.toLowerCase().includes(q) ||
          t.category.toLowerCase().includes(q)
        );
      })
    : templates;

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner className="h-6 w-6" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-secondary" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search templates..."
          className="w-full rounded-xl border border-border bg-surface py-3 pl-10 pr-4 text-base text-text placeholder:text-text-secondary/50 focus:outline-none focus:ring-2 focus:ring-border-focus"
        />
      </div>

      {!search.trim() && (
        <div className="grid gap-3 sm:grid-cols-2">
          {onGenerateWithAi && (
            <button
              type="button"
              onClick={onGenerateWithAi}
              className="flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-warning/50 p-6 text-center transition-colors hover:border-warning hover:bg-warning/5"
            >
              <Sparkles className="h-6 w-6 text-warning" />
              <p className="text-base font-semibold text-text">
                Generate with AI
              </p>
              <p className="text-sm text-text-secondary">
                Describe a theme and let AI build your list
              </p>
            </button>
          )}
          <button
            type="button"
            onClick={onCreateCustom}
            className="flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border p-6 text-center transition-colors hover:border-primary hover:bg-surface-raised"
          >
            <PenLine className="h-6 w-6 text-primary" />
            <p className="text-base font-semibold text-text">Create Your Own</p>
            <p className="text-sm text-text-secondary">
              Build a custom item list from scratch
            </p>
          </button>
        </div>
      )}

      {!search.trim() && (
        <>
          <div className="flex items-center gap-3">
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs font-medium uppercase tracking-wider text-text-secondary">
              Presets
            </span>
            <div className="h-px flex-1 bg-border" />
          </div>

          <div className="flex gap-1 rounded-lg bg-surface-raised p-1">
            <button
              type="button"
              onClick={() => setSort("newest")}
              className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                sort === "newest"
                  ? "bg-surface text-text shadow-sm"
                  : "text-text-secondary hover:text-text"
              }`}
            >
              Newest
            </button>
            <button
              type="button"
              onClick={() => setSort("popular")}
              className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                sort === "popular"
                  ? "bg-surface text-text shadow-sm"
                  : "text-text-secondary hover:text-text"
              }`}
            >
              Most Popular
            </button>
          </div>
        </>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        {filtered.map((t) => (
          <button
            key={t.templateId}
            type="button"
            onClick={() => onSelectTemplate(t)}
            className="rounded-xl border border-border p-4 text-left transition-colors hover:border-primary hover:bg-surface-raised"
          >
            <div className="flex items-start gap-3">
              <span className="text-2xl">{t.emoji}</span>
              <div className="min-w-0 flex-1">
                <p className="text-base font-semibold text-text">{t.name}</p>
                <p className="mt-0.5 text-sm text-text-secondary">
                  {t.description}
                </p>
                <div className="mt-1.5 flex items-center gap-3">
                  <span className="inline-flex items-center gap-1 text-sm text-text-secondary">
                    <ListChecks className="h-3.5 w-3.5" />
                    {t.itemCount} items
                  </span>
                  {t.usageCount > 0 && (
                    <span className="inline-flex items-center gap-1 text-sm text-text-secondary">
                      <Users className="h-3.5 w-3.5" />
                      Used {t.usageCount} {t.usageCount === 1 ? "time" : "times"}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </button>
        ))}
      </div>

      {filtered.length === 0 && search.trim() && (
        <div className="py-8 text-center">
          <p className="text-text-secondary">
            No templates match your search.
          </p>
          <button
            type="button"
            onClick={onCreateCustom}
            className="mt-2 text-sm font-medium text-primary hover:underline"
          >
            Create a custom list instead
          </button>
        </div>
      )}

      {filtered.length === 0 && !search.trim() && (
        <div className="py-8 text-center">
          <p className="text-text-secondary">No templates available yet.</p>
        </div>
      )}

      {nextCursor && !search.trim() && (
        <div className="flex justify-center pt-2">
          <button
            type="button"
            onClick={handleLoadMore}
            disabled={loadingMore}
            className="rounded-lg border border-border px-6 py-2 text-sm font-medium text-text transition-colors hover:bg-surface-raised disabled:opacity-50"
          >
            {loadingMore ? (
              <span className="inline-flex items-center gap-2">
                <Spinner className="h-4 w-4" />
                Loading...
              </span>
            ) : (
              "Show More"
            )}
          </button>
        </div>
      )}
    </div>
  );
}
